/**
 * Permission Utilities
 *
 * Provides permission checking functions with wildcard support.
 * Matches the backend permission checking logic.
 *
 * Permission Format: ResourceScopedPermission[]
 * Each permission group has an array of actions and array of resource patterns.
 * Multiple actions can share the same resource patterns.
 *
 * Action Wildcards:
 * - "*" = all permissions (full admin)
 * - "domain:*" = all permissions in domain (e.g., "volume:*", "elt:*")
 *
 * Resource Pattern Wildcards:
 * - "*" = all resources
 * - "prefix-*" = resources starting with prefix
 * - "*-suffix" = resources ending with suffix
 */

import { ResourceScopedPermission } from '@/types/role.types';

/**
 * Check if a resource pattern matches a target resource
 *
 * Supports AWS-style wildcards:
 * - "*" matches everything
 * - "prefix-*" matches anything starting with "prefix-"
 * - "*-suffix" matches anything ending with "-suffix"
 * - "prefix-*-suffix" matches with wildcards in middle
 */
function matchResourcePattern(pattern: string, resource: string): boolean {
  // Full wildcard matches everything
  if (pattern === '*') {
    return true;
  }

  // Exact match
  if (pattern === resource) {
    return true;
  }

  // No wildcards - must be exact match (already checked above)
  if (!pattern.includes('*')) {
    return false;
  }

  // Convert pattern to regex
  const regexPattern = pattern
    .split('*')
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape special regex chars
    .join('.*');
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(resource);
}

/**
 * Check if a permission action matches a required permission
 *
 * Supports wildcards:
 * - "*" matches everything
 * - "domain:*" matches all in domain (e.g., "volume:*")
 */
function matchPermissionAction(userPermission: string, required: string): boolean {
  // Full wildcard
  if (userPermission === '*') {
    return true;
  }

  // Exact match
  if (userPermission === required) {
    return true;
  }

  // Partial wildcard (e.g., "iam:*" or "iam:user:*")
  if (userPermission.endsWith(':*')) {
    const prefix = userPermission.slice(0, -1); // Keep the ':' before '*'
    if (required.startsWith(prefix)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if user has a specific permission for a specific resource
 *
 * @param permissions - Array of ResourceScopedPermission the user has
 * @param required - The permission action to check for
 * @param targetResource - Optional specific resource to check access for
 * @returns true if user has the permission (directly or via wildcard)
 *
 * @example
 * const perms = [{ actions: ["iam:GetUser"], resources: ["*"] }];
 * hasPermission(perms, 'iam:GetUser') // true
 * hasPermission(perms, 'iam:GetUser', 'user:123') // true
 *
 * const scopedPerms = [{ actions: ["application:DescribeApplication"], resources: ["application:inventory"] }];
 * hasPermission(scopedPerms, 'application:DescribeApplication', 'application:inventory') // true
 * hasPermission(scopedPerms, 'application:DescribeApplication', 'application:orders') // false
 */
export function hasPermission(
  permissions: ResourceScopedPermission[] | undefined,
  required: string,
  targetResource?: string
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  for (const perm of permissions) {
    // Skip if actions is not a valid array
    if (!perm.actions || !Array.isArray(perm.actions)) {
      continue;
    }

    // Check if any action in this permission group matches
    const hasMatchingAction = perm.actions.some((action) =>
      matchPermissionAction(action, required)
    );
    if (!hasMatchingAction) {
      continue;
    }

    // If no specific resource requested, just check action match
    if (!targetResource) {
      return true;
    }

    // Check if any resource pattern matches the target
    if (perm.resources && Array.isArray(perm.resources)) {
      for (const pattern of perm.resources) {
        if (matchResourcePattern(pattern, targetResource)) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Check if user has any of the specified permissions
 *
 * @param permissions - Array of ResourceScopedPermission the user has
 * @param required - Array of permissions to check (any match = true)
 * @returns true if user has at least one of the required permissions
 */
export function hasAnyPermission(
  permissions: ResourceScopedPermission[] | undefined,
  required: string[]
): boolean {
  if (!permissions || permissions.length === 0 || required.length === 0) {
    return false;
  }

  return required.some((req) => hasPermission(permissions, req));
}

/**
 * Check if user has all of the specified permissions
 *
 * @param permissions - Array of ResourceScopedPermission the user has
 * @param required - Array of permissions to check (all must match)
 * @returns true if user has all of the required permissions
 */
export function hasAllPermissions(
  permissions: ResourceScopedPermission[] | undefined,
  required: string[]
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  if (required.length === 0) {
    return true;
  }

  return required.every((req) => hasPermission(permissions, req));
}

/**
 * Check if user is a full admin (has wildcard permission for all resources)
 *
 * @param permissions - Array of ResourceScopedPermission the user has
 * @returns true if user has the '*' action with '*' resources
 */
export function isFullAdmin(permissions: ResourceScopedPermission[] | undefined): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  return permissions.some(
    (p) => p.actions?.includes('*') && p.resources?.includes('*')
  );
}

/**
 * Get the domain from a permission string
 *
 * @param permission - Permission string in format "domain:resource:action"
 * @returns The domain part or null if invalid
 */
export function getPermissionDomain(permission: string): string | null {
  const parts = permission.split(':');
  return parts.length >= 1 ? parts[0] : null;
}

/**
 * Get the resource from a permission string
 *
 * @param permission - Permission string in format "domain:resource:action"
 * @returns The resource part or null if invalid
 */
export function getPermissionResource(permission: string): string | null {
  const parts = permission.split(':');
  return parts.length >= 2 ? parts[1] : null;
}

/**
 * Get the action from a permission string
 *
 * @param permission - Permission string in format "domain:resource:action"
 * @returns The action part or null if invalid
 */
export function getPermissionAction(permission: string): string | null {
  const parts = permission.split(':');
  return parts.length >= 3 ? parts[2] : null;
}

/**
 * Get all resources a user can access for a specific permission
 *
 * @param permissions - Array of ResourceScopedPermission the user has
 * @param permissionAction - The permission action to check
 * @returns Array of resource patterns the user has access to, or ['*'] if full access
 */
export function getAccessibleResources(
  permissions: ResourceScopedPermission[] | undefined,
  permissionAction: string
): string[] {
  if (!permissions || permissions.length === 0) {
    return [];
  }

  const resources: Set<string> = new Set();

  for (const perm of permissions) {
    // Skip if actions is not a valid array
    if (!perm.actions || !Array.isArray(perm.actions)) {
      continue;
    }

    // Check if any action in this permission group matches
    const hasMatchingAction = perm.actions.some((action) =>
      matchPermissionAction(action, permissionAction)
    );
    if (hasMatchingAction && perm.resources && Array.isArray(perm.resources)) {
      // If any permission grants full access, return early
      if (perm.resources.includes('*')) {
        return ['*'];
      }
      perm.resources.forEach((r) => resources.add(r));
    }
  }

  return Array.from(resources);
}

/**
 * Merge multiple permission arrays (from multiple roles) into a single array
 * Used when a user has multiple roles and we need their effective permissions
 * Permissions with the same resource patterns get their actions combined
 *
 * @param permissionArrays - Multiple arrays of ResourceScopedPermission
 * @returns Merged array with combined actions for same resource patterns
 */
export function mergePermissions(
  ...permissionArrays: (ResourceScopedPermission[] | undefined)[]
): ResourceScopedPermission[] {
  // Use resource pattern string as key for grouping
  const resourceMap = new Map<string, Set<string>>();

  for (const permissions of permissionArrays) {
    if (!permissions) continue;

    for (const perm of permissions) {
      // Skip if actions or resources is not a valid array
      if (!perm.actions || !Array.isArray(perm.actions) || !perm.resources || !Array.isArray(perm.resources)) {
        continue;
      }

      // Create a canonical key from sorted resources
      const resourceKey = [...perm.resources].sort().join('|');
      const existing = resourceMap.get(resourceKey);
      if (existing) {
        perm.actions.forEach((action) => existing.add(action));
      } else {
        resourceMap.set(resourceKey, new Set(perm.actions));
      }
    }
  }

  return Array.from(resourceMap.entries()).map(([resourceKey, actions]) => ({
    actions: Array.from(actions),
    resources: resourceKey.split('|'),
  }));
}

/**
 * Extract all unique permission actions from ResourceScopedPermission array
 * Flattens all actions arrays into a single array of unique actions
 *
 * @param permissions - Array of ResourceScopedPermission
 * @returns Array of unique permission action strings
 */
export function extractPermissionActions(
  permissions: ResourceScopedPermission[] | undefined
): string[] {
  if (!permissions || permissions.length === 0) {
    return [];
  }

  const actions = new Set<string>();
  for (const perm of permissions) {
    if (perm.actions && Array.isArray(perm.actions)) {
      perm.actions.forEach((action) => actions.add(action));
    }
  }
  return Array.from(actions);
}

/**
 * Check if a permission is resource-scopeable
 * Non-scopeable permissions (like tenant:DescribeSettings) only work with '*' resources
 *
 * @param permission - The permission action string
 * @returns true if the permission supports resource scoping
 */
export function isPermissionScopeable(permission: string): boolean {
  // List of non-scopeable permissions (tenant-wide only)
  const nonScopeablePermissions = new Set([
    'tenant:DescribeSettings',
    'tenant:UpdateSettings',
    'tenant:DescribeBilling',
    'tenant:UpdateBilling',
    'iam:CreateRole',
    'iam:GetRole',
    'iam:UpdateRole',
    'iam:DeleteRole',
    'iam:CreateTenantRole',
    'iam:GetTenantRole',
    'iam:UpdateTenantRole',
    'iam:DeleteTenantRole',
  ]);

  return !nonScopeablePermissions.has(permission);
}
